import React from 'react';
import _ from 'lodash';
import { useMutation, useQuery } from 'react-fetching-library';

import { Box, Button, Stack } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { plPL } from '@mui/x-data-grid';

import { sendBankStatements, sendBankStatementsSpreadsheet, sendBankStatementsCollect, getBankStatementsAction, deleteBankStatementAction } from '../../../services/request';
import Transactions from './Transactions';
import { bankStatementsColumns } from '../../../services/column-definitions';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { AlertDialogContainer } from '../../../components/others/AlertDialogContainer';
import toast from 'react-hot-toast';
import { BankStatementsSidebar } from './bank-statements/BankStatementsSidebar';

const BankStatements = () => {
  const [filters, setFilters] = React.useState('');
  const { loading, payload, error, query } = useQuery(getBankStatementsAction(filters));
  const { mutate } = useMutation(sendBankStatements);
  const { mutate: mutateCollect } = useMutation(sendBankStatementsCollect);
  const { mutate: mutateSpreadsheet } = useMutation(sendBankStatementsSpreadsheet);
  const { mutate: mutateBankStatement } = useMutation(deleteBankStatementAction);
  const refInputFile = React.useRef(null);
  const [transactions, setTransactions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [collect, setCollect] = React.useState(false);
  const [bankStatements, setBankStatements] = React.useState([]);
  const [idToDelete, setIdToDelete] = React.useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [openSidebar, setOpenSidebar] = React.useState(false);
  const [hideSidebar, setHideSidebar] = React.useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    query();
    setOpen(false);
  };

  const handleSend = async data => {
    const { error: mutateError, payload } = await mutate(data);
    if (!mutateError) {
      setCollect(false);
      setTransactions(payload.transactions);
      handleOpen();
      query();
    } else {
      toast.error('Nie udało się wczytać pliku');
    }
  };

  const handleSendCollect = async data => {
    const { error: mutateError, payload } = await mutateCollect(data);
    if (!mutateError) {
      setCollect(true);
      setTransactions(payload.transactions);
      handleOpen();
      query();
    } else {
      toast.error('Nie udało się wczytać pliku');
    }
  };

  const handleSendSpreadsheet = async data => {
    const { error: mutateError, payload } = await mutateSpreadsheet(data);
    if (!mutateError) {
      setTransactions(payload.transactions);
      handleOpen();
    } else {
      toast.error('Nie udało się wczytać pliku');
    }
  };

  const handleFileSelect = (e, collect) => {
    const r = new FileReader();
    let fileName = '';
    if (refInputFile.current.files[0]) {
      fileName = refInputFile.current.files[0].name;
    } else {
      fileName = e.target.files[0].name;
    }
    r.onload = () => {
      if (collect) {
        handleSendCollect({ 'mt940': r.result, 'filename': fileName });
      } else {
        handleSend({ 'mt940': r.result, 'filename': fileName });
      }
      refInputFile.current.value = null;
    };

    if (e.target.files[0].type === 'application/vnd.ms-excel' || e.target.files[0].type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      const formData = new FormData();
      formData.append('file', e.target.files[0]);
      handleSendSpreadsheet(formData);
      refInputFile.current.value = null;
    } else {
      r.readAsText(e.target.files[0], 'utf-8');
    }
  };

  const handleEdit = data => {
    setCollect(data.collect);
    setTransactions(data.transactions);
    handleOpen();
  };

  const handleDelete = async () => {
    const { error, payload } = await mutateBankStatement(idToDelete);
    if (!error) {
      const bsCopy = _.cloneDeep(bankStatements);
      const bsFiltered = _.filter(bsCopy, obj => obj.id !== idToDelete);
      setBankStatements(bsFiltered);
      setIdToDelete(null);
      setSeverity('success');
      setTitle('Usunięto wyciąg bankowy');
      setMessage('');
      setShowAlert(true);
    } else {
      setSeverity('error');
      setTitle('Nie udało się usunąć wyciągu bankowego');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleConfirmDelete = id => {
    setConfirmDeleteOpen(true);
    setIdToDelete(id);
  };

  const queryCallback = (filtersVal = '') => {
    setFilters(filtersVal);
  };

  const [columns, setColumns] = React.useState(bankStatementsColumns(handleEdit, handleConfirmDelete));

  React.useEffect(() => {
    if (!loading && !error) {
      setBankStatements(payload.results);
    }
  }, [loading, filters]);

  return (
    <Box marginTop={5}>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Button variant="contained" component="label">
      Wczytaj plik
        <input type="file" accept=".exp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={e => handleFileSelect(e, false)} ref={refInputFile}/>
      </Button>
      <Button variant="contained" component="label" style={{ marginLeft: 20 }}>
      Wczytaj plik Collect
        <input type="file" accept=".exp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .mt940" hidden onChange={e => handleFileSelect(e, true)} ref={refInputFile}/>
      </Button>
      <Transactions open={open} handleClose={handleClose} transactions={transactions} setTransactions={setTransactions} collect={collect} query={query} />
      <Stack direction="row" sx={{ height: 'calc(100% - 24px)' }}>
        { !hideSidebar && (
          <BankStatementsSidebar
            isOpenSidebar={openSidebar}
            onCloseSidebar={() => setOpenSidebar(false)}
            queryCallback={queryCallback}
          />
        )}
        <DataGridPro
          columns={columns}
          rows={bankStatements}
          density="compact"
          disableColumnMenu={true}
          autoPageSize={false}
          autoHeight={true}
          rowsPerPageOptions={[10, 25, 50, 100]}
          pagination
          localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
          // components={{ Toolbar: DataGridToolbar }}
          // componentsProps={{ toolbar: { containerStyle: { backgroundColor: 'transparent' }, searchStyle: { width: '100%' }, filterToolbar: false, columnsToolbar: false, densityToolbar: false, exportToolbar: false, miniView: true } } }
          disableSelectionOnClick
          loading={loading}
        />
      </Stack>
      <ConfirmDialog
        title="Usunąć Wyciąg Bankowy?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć ten wyciąg bankowy?
      </ConfirmDialog>
    </Box>
  );
};

export default BankStatements;

